import type { TiptapDoc } from "@klarity/schema";
import type { Editor } from "@tiptap/core";
import { EditorContent, useEditor } from "@tiptap/react";
import { useEffect, useMemo, useRef } from "react";
import { createEditorStores, editorExtensions } from "./extensions";
import { type EditorHost, setHost } from "./host";
import { SuggestionMenu } from "./SuggestionMenu";

export type KlarityEditorProps = {
  content: TiptapDoc;
  mode: "read" | "edit";
  host: EditorHost;
  onChange?: (editor: Editor) => void;
  onBlur?: (editor: Editor) => void;
  onReady?: (editor: Editor) => void;
};

export function KlarityEditor({ content, mode, host, onChange, onBlur, onReady }: KlarityEditorProps) {
  const callbacks = useRef({ onChange, onBlur, onReady });
  callbacks.current = { onChange, onBlur, onReady };
  const stores = useMemo(() => createEditorStores(), []);
  // biome-ignore lint/correctness/useExhaustiveDependencies: host changes are applied through setHost
  const extensions = useMemo(() => editorExtensions({ mode, host, stores }), [mode, stores]);

  const editor = useEditor(
    {
      extensions,
      content,
      editable: mode === "edit",
      injectCSS: false,
      editorProps: {
        attributes: {
          class: "kd-prose",
          "aria-label": "Document content",
          spellcheck: mode === "edit" ? "true" : "false",
        },
      },
      onUpdate: ({ editor: e }) => callbacks.current.onChange?.(e),
      onBlur: ({ editor: e }) => callbacks.current.onBlur?.(e),
    },
    [mode],
  );

  useEffect(() => {
    setHost(editor, host);
  }, [editor, host]);

  useEffect(() => {
    callbacks.current.onReady?.(editor);
  }, [editor]);

  return (
    <>
      <EditorContent editor={editor} className="kd-editor" />
      {mode === "edit" ? (
        <>
          <SuggestionMenu
            store={stores.slash}
            label="Insert block"
            empty="No block matches"
            itemKey={(i) => i.id}
            renderItem={(i) => (
              <span className="flex items-baseline justify-between gap-3">
                <span className="text-ink">{i.label}</span>
                <span className="truncate text-xs text-ink-3">{i.hint}</span>
              </span>
            )}
          />
          <SuggestionMenu
            store={stores.links}
            label="Link a document"
            empty="No document with a code matches"
            itemKey={(d) => d.code}
            renderItem={(d) => (
              <span className="flex flex-col">
                <span className="kd-codetag self-start">{d.code}</span>
                <span className="truncate text-ink">{d.title}</span>
                <span className="text-xs text-ink-3">{d.spaceName}</span>
              </span>
            )}
          />
        </>
      ) : null}
    </>
  );
}
